import {
    EBookingLimitListingState,
    IBookingLimitRule,
    TBookingLimitPeriodType,
} from 'types/bookingLimit';

/** 目前只有 WEEKLY，但后端按周期类型分行返回，取值走查找而不是下标。 */
export const WEEKLY: TBookingLimitPeriodType = 'WEEKLY';

export const findRule = (rules?: IBookingLimitRule[]): IBookingLimitRule | undefined =>
    rules?.find((r) => r.periodType === WEEKLY);

/**
 * 非法时间一律返回空串，让调用方渲染成空白。
 *
 * 必须查 Date 本身而不是 Number.isFinite(epochMs)：8.7e15 是有限数，
 * 但 new Date(8.7e15) 是 Invalid Date，Intl 的 format 对它会抛 RangeError。
 */
const invalidAt = (epochMs: number): boolean => Number.isNaN(new Date(epochMs).getTime());

const RESET_FORMAT: Intl.DateTimeFormatOptions = {
    weekday: 'long',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
    // shortGeneric 给「PT」，short 会给随夏令时变化的「PDT」/「PST」——设计稿要前者
    timeZoneName: 'shortGeneric',
};

/**
 * 「Monday, 12:00 AM PT」。
 *
 * **必须用后端返回的 timezone**，那是 practice owner 的时区，未必是当前用户的：
 * 成员在阿拉斯加、owner 在上海时，重置时刻是 owner 的周一零点。
 * 用浏览器时区推算会显示成错误的日期。
 */
export const formatResetAt = (epochMs: number, timeZone: string): string => {
    if (invalidAt(epochMs)) {
        return '';
    }
    const render = (tz?: string): string => {
        const parts = new Intl.DateTimeFormat('en-US', { ...RESET_FORMAT, timeZone: tz })
            .formatToParts(new Date(epochMs));
        const get = (t: string) => parts.find((p) => p.type === t)?.value ?? '';
        return `${get('weekday')}, ${get('hour')}:${get('minute')} ${get('dayPeriod')} ${get('timeZoneName')}`.trim();
    };
    try {
        return render(timeZone);
    } catch (e) {
        // 只有非法时区串会走到这里（时间已在上面挡掉），退到浏览器时区
        return render(undefined);
    }
};

/**
 * 修改历史的时间：「Aug 28, 2026, 9:14 AM PT」。
 *
 * 按**浏览器时区**展示 —— 这是操作记录（谁在什么时候改的），
 * 与按 practice owner 时区计算的周期窗口是两回事，弹窗副标题也明说了
 * 「Times are in your timezone」。
 *
 * 不传 timeZone，所以构造函数不会抛；时间挡在 invalidAt 里。
 * 原先那个 catch 里用 toISOString() 兜底是错的 —— 它对 Invalid Date
 * 同样抛 RangeError，异常会穿出去把整个历史弹窗打白。
 */
export const formatHistoryAt = (epochMs: number): string => {
    if (invalidAt(epochMs)) {
        return '';
    }
    return new Intl.DateTimeFormat('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
        timeZoneName: 'shortGeneric',
    }).format(new Date(epochMs));
};

/** 美分 → $1,080。不显示小数：lead gen fee 都是整数美元。 */
export const formatSpent = (cents: number): string =>
    `$${Math.round(cents / 100).toLocaleString('en-US')}`;

/** 被本功能暂停（含被扣住的恢复请求）。两者对外表现一致，都从搜索消失。 */
export const isPausedByLimit = (state?: EBookingLimitListingState): boolean =>
    state === EBookingLimitListingState.PAUSED_BY_LIMIT ||
    state === EBookingLimitListingState.PAUSED_BY_LIMIT_HELD;

/** 上限控件是否该整体置灰：没上架、或整个 practice 被暂停时，调上限没有意义。 */
export const isLimitIrrelevant = (state?: EBookingLimitListingState): boolean =>
    state === EBookingLimitListingState.NOT_LISTED ||
    state === EBookingLimitListingState.PRACTICE_PAUSED;

export const MIN_LIMIT = 1;
export const MAX_LIMIT = 999;

/**
 * 草稿校验。文案取自产品原型，与后端的
 * 「Limit must be between 1 and 999.」不同 —— 上界由输入框 max 挡住，
 * 正常操作走不到，所以这里只讲下界，并给出「要完全停收新患者就关掉开关」的出路。
 */
export const validateDraft = (enabled: boolean, limitCount: number | null): string | null => {
    if (!enabled) {
        return null;
    }
    if (limitCount === null || Number.isNaN(limitCount)) {
        return 'Enter a number.';
    }
    if (limitCount < MIN_LIMIT || limitCount > MAX_LIMIT) {
        return 'The limit has to be at least 1. To stop taking new patients entirely, turn the limit off.';
    }
    return null;
};
